import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { APP_NAME } from '../../App';
// Breadcrumbs import removed
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Container,
  useMediaQuery,
  useTheme,
  Button,
  Popover,
  ListSubheader,
  Skeleton,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  CardMedia,
  Grid
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import LanguageIcon from '@mui/icons-material/Language';
import SchoolIcon from '@mui/icons-material/School';
import CustomAddIcon from './CustomAddIcon';
import CustomDashboardIcon from './CustomDashboardIcon';
import ShowIcon from './ShowIcon';
import HideIcon from './HideIcon';
import ProfileImageUpload from './ProfileImageUpload';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import CodeIcon from '@mui/icons-material/Code';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import DevicesIcon from '@mui/icons-material/Devices';
import PublicIcon from '@mui/icons-material/Public';
import HomeIcon from '@mui/icons-material/Home';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockIcon from '@mui/icons-material/Lock';

import useAuth from '../../hooks/useAuth';
import useNotification from '../../hooks/useNotification';
import useProject from '../../hooks/useProject';
import useLanguage from '../../hooks/useLanguage';
import LanguageSwitcher from '../common/LanguageSwitcher';
import PermissionGate from '../common/PermissionGate';
import NotificationBell from './NotificationBell';
import projectTypeService from '../../services/projectTypeService';
import ImageGeneratorDialog from './ImageGeneratorDialog';
import ImageEditorDialog from './ImageEditorDialog';
import ProjectTypeSelectionDialog from './ProjectTypeSelectionDialog';

const drawerWidth = 220;

// Empty default - projects will be loaded from API
const DEFAULT_PROJECTS = [];

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { showNotification } = useNotification();
  const { projects: projectData, refetchProjects, updateProject, isLoadingProjects, setFilters } = useProject();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for project and document names to be used in breadcrumbs
  const [projectName, setProjectName] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [projects, setProjects] = useState(DEFAULT_PROJECTS);
  const [projectsVisible, setProjectsVisible] = useState(true); // Set to true by default
  const [lastVisitedProject, setLastVisitedProject] = useState(null);
  
  // Initialize drawer closed on mobile, open on desktop
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [plusAnchorEl, setPlusAnchorEl] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImageGeneratorDialog, setShowImageGeneratorDialog] = useState(false);
  const [showImageEditorDialog, setShowImageEditorDialog] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [projectTypes, setProjectTypes] = useState([]);
  const [profileImageError, setProfileImageError] = useState(false);
  
  // Log user information for debugging
  useEffect(() => {
    if (user) {      
      // Reset the image error state when user or profileImage changes
      setProfileImageError(false);
      
      // Test loading the image
      if (user.profileImage) {
        const img = new Image();
        img.onload = () => console.log('Profile image loaded successfully');
        img.onerror = (e) => {
          console.error('Error loading profile image:', e);
          setProfileImageError(true);
        };
        img.src = user.profileImage;
      }
    }
  }, [user, user?.profileImage]);

  // Load project types
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        // Custom project types based on the image provided
        const customTypes = [
          {
            _id: 'application',
            id: 'application',
            name: 'Application',
            icon: <CodeIcon />,
            isActive: true
          },
          {
            _id: 'website',
            id: 'website',
            name: 'Website',
            icon: <LanguageIcon />,
            isActive: true
          },
          {
            _id: 'orchestrator-crew',
            id: 'orchestrator-crew',
            name: 'Agentic crew',
            icon: <GroupsIcon />,
            isActive: true
          },
          {
            _id: 'knowledge-hub',
            id: 'knowledge-hub',
            name: 'Knowledge hub',
            icon: <SchoolIcon />,
            isActive: false
          },
          {
            _id: 'image',
            id: 'image',
            name: 'Image',
            icon: <ImageIcon />,
            isActive: false
          }
        ];
        
        setProjectTypes(customTypes);
      } catch (error) {
        console.error('Error setting project types:', error);
        showNotification({
          message: `Error setting project types: ${error.message}`,
          type: 'error'
        });
      }
    };
    
    fetchProjectTypes();
  }, [showNotification]);
  
  // Update drawer state when screen size changes - desktop only
  useEffect(() => {
    if (!isMobile) {
      setDrawerOpen(true); // Only auto-open on desktop
    } else {
      setDrawerOpen(false); // Make sure it's closed on mobile
    }
  }, [isMobile]);
  
  // Set sidebar filters when user is available
  useEffect(() => {
    if (user) {
      setFilters({ status: 'active', limit: 20 });
    }
  }, [user, setFilters]);

  // Sync sidebar projects whenever projectData changes
  useEffect(() => {
    if (isLoadingProjects) return;

    if (projectData && projectData.length > 0) {
      const activeProjects = projectData.filter(project => project.status === 'active');

      // Get the last visited project from localStorage
      const storedLastProject = localStorage.getItem('lastAccessedProject');
      let lastProjectId = null;
      if (storedLastProject) {
        try {
          const parsed = JSON.parse(storedLastProject);
          lastProjectId = parsed.id;
        } catch (e) {
          console.error("Error parsing stored project:", e);
        }
      }

      // Sort: last visited first, then by updatedAt
      const sortedProjects = [...activeProjects].sort((a, b) => {
        if (a._id === lastProjectId) return -1;
        if (b._id === lastProjectId) return 1;
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });

      // Save last accessed project to localStorage
      if (sortedProjects[0]) {
        const lastProject = {
          id: sortedProjects[0]._id,
          name: sortedProjects[0].name,
          status: sortedProjects[0].status
        };
        localStorage.setItem('lastAccessedProject', JSON.stringify(lastProject));
        setLastVisitedProject(lastProject);
      }

      // Format and take first 5
      const pathMatch = location.pathname.match(/\/projects\/([a-f0-9]+)/i);
      const currentProjectId = pathMatch ? pathMatch[1] : null;

      const formattedProjects = sortedProjects
        .slice(0, 5)
        .map((project) => ({
          id: project._id,
          name: project.name,
          shortCode: project.shortCode,
          status: project.status || 'active',
          lastVisited: project._id === lastProjectId,
          active: currentProjectId ? project._id === currentProjectId : project._id === lastProjectId
        }));

      setProjects(formattedProjects);
    } else {
      // No projects from API - clear the list and cached data
      setProjects([]);
      localStorage.removeItem('lastAccessedProject');
      setLastVisitedProject(null);
    }
  }, [projectData, isLoadingProjects, location.pathname]);
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handlePlusMenuOpen = (event) => {
    // Instead of opening a popover, show the updated dialog
    setShowCreateDialog(true);
  };
  
  const handlePlusMenuClose = () => {
    setPlusAnchorEl(null);
  };
  
  const handleCreateDialogClose = () => {
    setShowCreateDialog(false);
  };

  const handleProjectTypeSelection = ({ projectType, workflowType }) => {
    handleCreateDialogClose();
    
    // Navigate to create project page with the selected type and workflow
    const query = new URLSearchParams();
    query.set('type', projectType);
    if (workflowType) {
      query.set('workflow', workflowType);
    }
    
    navigate(`/projects/create?${query.toString()}`);
  };
  
  const handleImageGeneratorDialogClose = () => {
    setShowImageGeneratorDialog(false);
  };
  
  const handleImageEditorDialogClose = () => {
    setShowImageEditorDialog(false);
    setSelectedImageUrl('');
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      showNotification({
        message: 'Logged out successfully',
        type: 'success'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      showNotification({
        message: 'Logout failed',
        type: 'error'
      });
    }
    handleProfileMenuClose();
  };
  
  const handleNavigation = (path, projectData = null) => {
    // If this is a project navigation, update the last accessed project
    if (projectData && path.includes('/projects/')) {
      
      // Store the project as last accessed
      localStorage.setItem('lastAccessedProject', JSON.stringify({
        id: projectData.id,
        name: projectData.name,
        status: projectData.status || 'active'
      }));
      
      // Update the last visited status in the backend
      updateProject(projectData.id, { lastVisited: new Date() })
        .then(() => {
          
          // Refresh the projects list to ensure our sidebar shows the correct order
          // Use a small timeout to avoid excessive API calls if user clicks rapidly
          setTimeout(() => {
            refetchProjects();
          }, 300);
        })
        .catch(err => {
          console.error(`MainLayout: Error updating last visited for project:`, err);
        });
      
      // Set as the last visited project
      setLastVisitedProject(projectData);
      
      // Update the active project in the current projects list
      const updatedProjects = projects.map(p => ({
        ...p,
        active: p.id === projectData.id,
        lastVisited: p.id === projectData.id
      }));
      setProjects(updatedProjects);
    }
    
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#485869', // Slate blue-gray from theme
      color: 'white'
    }}>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', // Center everything in the toolbar
        px: 2,
        height: 72,
        color: 'white'
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            height: '72px', // Match toolbar height for vertical centering
            justifyContent: 'center',
            flex: 1
          }}
          onClick={() => handleNavigation('/projects')}
        >
          <img src="/logo-white.svg" alt="Ailyce" style={{ height: '50px' }} />
        </Box>
        {isMobile && (
          <IconButton 
            onClick={handleDrawerToggle} 
            size="small" 
            sx={{ 
              color: 'white',
              position: 'absolute',
              right: 8
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid #ffffff' }} />
      
      {/* My Projects Section */}
      <Box sx={{ px: 3, py: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" component="h2" fontWeight={600} color="white" sx={{ fontSize: '1.1rem' }}>
            My projects
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              color: 'white',
              padding: '4px'
            }}
            onClick={() => setProjectsVisible(!projectsVisible)}
            title={projectsVisible ? "Show only last visited" : "Show all projects"}
          >
            <ShowIcon fill="#a2abb3" sx={{ width: '21px', height: '16px' }} />
          </IconButton>
        </Box>
        
        {isLoadingProjects ? (
          <>
            <Typography sx={{ fontSize: '0.9rem', color: 'white', mb: 3, fontWeight: 400 }}>
              Last visited
            </Typography>
            <List sx={{ p: 0, mt: 1 }}>
              {Array(3).fill(0).map((_, index) => (
                <ListItem key={`skeleton-${index}`} disablePadding sx={{ mb: 0.75 }}>
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Skeleton variant="text" width="90%" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ width: 10, ml: 1 }}></Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </>
        ) : projects.length > 0 ? (
          <>
            <Typography sx={{ fontSize: '0.9rem', color: 'white', mb: 3, fontWeight: 400 }}>
              Last visited
            </Typography>
            <List sx={{ p: 0, mt: 1 }}>
              {(projectsVisible ? projects : projects.filter(p => p.lastVisited)).map((project) => (
                <ListItem
                  key={project.id}
                  disablePadding
                  sx={{ mb: 0.75 }}
                >
                  <ListItemButton
                    onClick={() => handleNavigation(`/projects/${project.id}`, project)}
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: '0.25rem',
                      color: 'white',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 'var(--font-size-15px)',
                          color: project.active ? 'white' : '#a7afb7',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flexGrow: 1,
                          fontWeight: project.active ? 500 : 400
                        }}
                      >
                        {project.shortCode ?
                          `${project.shortCode.substring(0, 2)} - ${project.name}` :
                          `${project.name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()} - ${project.name}`
                        }
                      </Typography>
                    </Box>
                    <svg
                      width="10"
                      height="13"
                      viewBox="0 0 10 13"
                      fill={project.active ? "#FFFFFF" : "#A7AFB7"}
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: '4px' }}
                    >
                      <path d="M9.71398 7.03462L1.04614 12.8845C0.946112 12.952 0.829052 12.9915 0.707639 12.9988C0.586226 13.006 0.465085 12.9808 0.357335 12.9258C0.249584 12.8707 0.159331 12.788 0.0963449 12.6866C0.0333595 12.5851 4.19617e-05 12.4688 9.53674e-07 12.3502V0.65042C-8.01086e-05 0.531782 0.0331488 0.415385 0.0960855 0.313844C0.159022 0.212302 0.249268 0.12949 0.357039 0.074381C0.464812 0.019272 0.586 -0.00603138 0.707472 0.00121319C0.828943 0.00845776 0.946065 0.0479739 1.04614 0.115479L9.71398 5.96538C9.80228 6.02546 9.8744 6.10544 9.9242 6.19852C9.974 6.2916 10 6.39502 10 6.5C10 6.60498 9.974 6.7084 9.9242 6.80148C9.8744 6.89456 9.80228 6.97454 9.71398 7.03462Z" fill={project.active ? "#FFFFFF" : "#A7AFB7"}></path>
                    </svg>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .text': {
                        color: 'white',
                        textDecoration: 'underline'
                      }
                    }
                  }}
                  onClick={() => handleNavigation('/projects')}
                >
                  <Typography
                    className="text"
                    variant="caption"
                    component="span"
                    sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}
                  >
                    All my projects
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: '0.85rem',
              color: 'rgba(255,255,255,0.5)',
              mt: 2,
              lineHeight: 1.6
            }}
          >
            No projects available. Please create a new project.
          </Typography>
        )}
      </Box>
      
      {/* My Images Section - Hidden as requested */}
      {/* Removed as per request to hide My Images in left navigation */}
      
      {/* Prompts Section removed */}
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        color="inherit"
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          bgcolor: '#485869', // Match drawer background color
          color: 'white',
          mt: 0, // Remove margin from top
          pr: 6
        }}
      >
        <Toolbar sx={{ height: 72 }}>
          {/* Hamburger menu button for mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1, 
              cursor: 'pointer',
              height: '72px', // Match toolbar height for vertical centering
              justifyContent: 'flex-start'
            }} 
            onClick={() => handleNavigation('/projects')}
          >
            <img src="/logo-white.svg" alt="Ailyce" style={{ height: '50px' }} />
          </Box>
          
          {/* Dashboard button */}
          <IconButton
            onClick={() => handleNavigation('/projects')}
            sx={{ 
              mr: 1, 
              color: 'white',
              '&:hover': {
                bgcolor: 'transparent',
              }
            }}
          >
            <CustomDashboardIcon color="inherit" />
          </IconButton>
          
          {/* Plus button */}
          <PermissionGate permission="header.createproject">
            <IconButton
              aria-label="add new"
              onClick={handlePlusMenuOpen}
              sx={{
                mr: 1,
                color: 'white',
                '&:hover': {
                  bgcolor: 'transparent',
                }
              }}
            >
              <CustomAddIcon color="inherit" />
            </IconButton>
          </PermissionGate>

          {/* User Management icon - only visible for users with permission */}
          <PermissionGate permission="admin.user.management">
            <IconButton
              aria-label="user management"
              onClick={() => {
                console.log('Navigating to users page...');
                navigate('/users');
              }}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'transparent',
                }
              }}
              title="User Management"
            >
              <GroupsIcon />
            </IconButton>
          </PermissionGate>

          {/* Notification Bell */}
          <NotificationBell />

          {/* New Project Type Selection Dialog */}
          <ProjectTypeSelectionDialog
            open={showCreateDialog}
            onClose={handleCreateDialogClose}
            onCreateProject={handleProjectTypeSelection}
          />
          
          {/* User profile */}
          {/* Language Switcher */}
          {/* <LanguageSwitcher /> */}
          
          {/* User Profile */}
          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{ 
              p: 0.5,
              border: '2px solid',
              borderColor: 'rgba(255,255,255,0.2)',
              ml: 1
            }}
          >
            {user?.profileImage && !profileImageError ? (
              <Box 
                component="img"
                key={user.profileImage} // Force re-render when image URL changes
                src={user.profileImage}
                alt={user?.name || 'User'}
                onError={() => setProfileImageError(true)}
                sx={{ 
                  width: 32, 
                  height: 32,
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            )}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              elevation: 3,
              sx: { 
                mt: 1, 
                width: 250,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              }
            }}
          >
            <ProfileImageUpload size={80} onClose={handleProfileMenuClose} />
            <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'user@example.com'}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate('/profile');
            }}
            sx={{ 
              borderRadius: '0.25rem',
              mx: 1,
              my: 0.5
            }}
            >
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <PermissionGate permission="admin.settings.page">
              <MenuItem onClick={() => {
                handleProfileMenuClose();
                navigate('/settings');
              }}
              sx={{
                borderRadius: '0.25rem',
                mx: 1,
                my: 0.5
              }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" sx={{ color: 'grey.700' }} />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
            </PermissionGate>

            {/* Archived Projects */}
            <PermissionGate permission="admin.archived.projects">
              <MenuItem onClick={() => {
                handleProfileMenuClose();
                navigate('/projects/archived');
              }}
              sx={{
                borderRadius: '0.25rem',
                mx: 1,
                my: 0.5
              }}
              >
                <ListItemIcon>
                  <FolderIcon fontSize="small" sx={{ color: 'grey.700' }} />
                </ListItemIcon>
                <ListItemText>Archived projects</ListItemText>
              </MenuItem>
            </PermissionGate>

            {/* User Management */}
            <PermissionGate permission="admin.user.management">
              <MenuItem onClick={() => {
                handleProfileMenuClose();
                navigate('/users');
              }}
              sx={{
                borderRadius: '0.25rem',
                mx: 1,
                my: 0.5
              }}
              >
                <ListItemIcon>
                  <GroupsIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText>User management</ListItemText>
              </MenuItem>
            </PermissionGate>

            {/* Feedback Management */}
            <PermissionGate permission="admin.feedback">
              <MenuItem onClick={() => {
                handleProfileMenuClose();
                navigate('/admin/feedback');
              }}
              sx={{
                borderRadius: '0.25rem',
                mx: 1,
                my: 0.5
              }}
              >
                <ListItemIcon>
                  <FeedbackIcon fontSize="small" sx={{ color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText>Feedback management</ListItemText>
              </MenuItem>
            </PermissionGate>

            {/* User Permission */}
            <PermissionGate permission="admin.user.permission">
              <MenuItem onClick={() => {
                handleProfileMenuClose();
                navigate('/user-permission');
              }}
              sx={{
                borderRadius: '0.25rem',
                mx: 1,
                my: 0.5
              }}
              >
                <ListItemIcon>
                  <SecurityIcon fontSize="small" sx={{ color: '#666' }} />
                </ListItemIcon>
                <ListItemText>User permission</ListItemText>
              </MenuItem>
            </PermissionGate>
            <Divider />
            <MenuItem onClick={handleLogout}
            sx={{ 
              borderRadius: '0.25rem',
              mx: 1,
              my: 0.5,
              color: 'error.main'
            }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {/* Use permanent drawer for desktop, temporary for mobile */}
      {isMobile ? (
        <Drawer
          variant="temporary" 
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
              bgcolor: '#485869', // Slate blue-gray
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent" 
          open={true}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: 'none',
              bgcolor: '#485869' // Slate blue-gray
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
      <Box
        component="main"
        className="main-content-box"
        sx={{
          flexGrow: 1,
          p: 1,
          width: '100%',
          marginLeft: 0,
          pt: { xs: '72px', sm: '72px' },
          pb: 2,
          bgcolor: 'background.default',
          minHeight: '100vh',
          m: 0,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ 
          width: '100%', 
          maxWidth: '1600px',
          mx: 'auto',
          height: '100%'
        }}>
          {/* Breadcrumbs removed from all pages */}
          
          {/* The Outlet receives projectName and documentName as context */}
          <Outlet context={[setProjectName, setDocumentName]} />
        </Box>
      </Box>
      
      {/* Groot components hidden
      <GrootCorner customPhrases={[
        { groot: "I am Groot!", translation: "Welcome to the PRD system!" },
        { groot: "I am Groot?", translation: "Need help creating your requirements?" },
        { groot: "I am Groot!", translation: "Click the chat button for more assistance." },
        { groot: "I am Groot...", translation: "Remember to save your work regularly!" }
      ]} />
      
      <GrootFloatingChat />
      */}
      
      {/* Floating action buttons removed */}
      
      {/* Image Generator Dialog */}
      <ImageGeneratorDialog
        open={showImageGeneratorDialog}
        onClose={handleImageGeneratorDialogClose}
        projectId={lastVisitedProject?.id}
      />
      
      {/* Image Editor Dialog */}
      <ImageEditorDialog
        open={showImageEditorDialog}
        onClose={handleImageEditorDialogClose}
        projectId={lastVisitedProject?.id}
        initialImageUrl={selectedImageUrl}
      />
    </Box>
  );
};

export default MainLayout;